import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import {
    WalletInfoResponse,
    WalletTransactionsResponse,
    RawWalletTransactionsResponse,
    WalletTabType
} from '@/src/types/wallet'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { mapRawTransaction } from '@/src/utils/wallet/transactionMapper'

/**
 * Determines the channel based on hostname
 * @returns 'Fly' for flyin domains, 'Ct' for cleartrip
 */
const getChannel = (): string => {
    return 'Fly'
}

const baseQuery = fetchBaseQuery({
    baseUrl: '',
    credentials: 'include',
    prepareHeaders: headers => {
        const defaultHeaders = setCommonHeaders(headers)
        defaultHeaders.set('X-CT-SOURCETYPE', 'web')
        return defaultHeaders
    }
})

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 })

export const walletApi = createApi({
    reducerPath: 'walletApi',
    baseQuery: baseQueryWithRetry,
    tagTypes: ['WalletInfo', 'WalletTransactions'],
    endpoints: builder => ({
        /**
         * Fetch wallet balance and domain balances
         */
        getWalletInfo: builder.query<WalletInfoResponse, { userId: string | number; currency: string }>({
            query: ({ userId, currency }) => ({
                url: `/pay/wallet`,
                params: {
                    userId,
                    currency,
                    chnl: getChannel()
                },
                method: 'GET'
            }),
            providesTags: ['WalletInfo']
        }),

        /**
         * Fetch wallet transactions with pagination
         */
        getWalletTransactions: builder.query<
            WalletTransactionsResponse,
            {
                userId: string | number
                currency: string
                type: WalletTabType
                offset: number
                size?: number
            }
        >({
            query: ({ userId, currency, type, offset, size = 10 }) => ({
                url: `/pay/wallet/${userId}/transactions`,
                params: {
                    offset,
                    size,
                    currency,
                    type,
                    chnl: getChannel()
                },
                method: 'GET'
            }),
            transformResponse: (response: RawWalletTransactionsResponse): WalletTransactionsResponse => {
                return {
                    status: 'SUCCESS',
                    transactions: response.transactions?.map(mapRawTransaction) || [],
                    totalRecords: response.totalRecords || 0
                }
            },
            transformErrorResponse: (response: any): WalletTransactionsResponse => {
                return {
                    status: 'FAILURE',
                    transactions: [],
                    totalRecords: 0,
                    message: response?.data?.message || 'Failed to fetch transactions'
                }
            },
            providesTags: (result, error, { type }) => [{ type: 'WalletTransactions', id: type }]
        })
    })
})

export const { useGetWalletInfoQuery, useGetWalletTransactionsQuery } = walletApi

export default walletApi
